import React, { useState } from "react";
import DSDialog from "../../system-design/DSDialog";
import CalendarEventPreview from "./CalendarEventPreview";
import CalendarEventEditView from "./CalendarEventEditView";
import useCalendarEventQuery from "../hooks/useCalendarEventQuery";
import { Edit } from "@mui/icons-material";

import { formatDateTime } from "../../../utils/util";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSDialogContent from "../../system-design/DSDialogContent";
import { PersonalFeedHistory } from "../../../api/personalFeed";
import {
  CalendarEvent,
  PopulatedCalendarEvent,
} from "../../../api/calendarEvent";

enum CalendarEventDialogMode {
  EDIT = "edit",
  PREVIEW = "preview",
}

export type WithRequiredId<T> = Partial<T> & { id: string };

type CalendarEventDialogProps = {
  calendarEvent:
    | WithRequiredId<PersonalFeedHistory>
    | WithRequiredId<PopulatedCalendarEvent>
    | WithRequiredId<CalendarEvent>;
  open: boolean;
  onClose: () => void;
};

const CalendarEventDialog = ({
  calendarEvent,
  open,
  onClose,
}: CalendarEventDialogProps) => {
  const [currentMode, setCurrentMode] = useState<CalendarEventDialogMode>(
    CalendarEventDialogMode.PREVIEW,
  );
  const { data, isLoading } = useCalendarEventQuery(calendarEvent.id);

  const isPersonalFeedHistory = (
    data:
      | WithRequiredId<PersonalFeedHistory>
      | WithRequiredId<PopulatedCalendarEvent>
      | WithRequiredId<CalendarEvent>,
  ): data is PersonalFeedHistory => {
    return "displayedKlydos" in data;
  };

  const handleModeSwitch = () => {
    setCurrentMode((prev) =>
      prev === CalendarEventDialogMode.PREVIEW
        ? CalendarEventDialogMode.EDIT
        : CalendarEventDialogMode.PREVIEW,
    );
  };
  const formattedDate = data ? formatDateTime(data.date) : "New";

  const DialogContent = () => {
    if (isLoading) {
      return (
        <DSDialogContent>
          <DSCircularProgress />
        </DSDialogContent>
      );
    }

    if (!data || currentMode === CalendarEventDialogMode.EDIT) {
      return (
        <CalendarEventEditView
          calendarEventId={calendarEvent.id}
          onClose={onClose}
        />
      );
    }
    return <CalendarEventPreview calendarEvent={calendarEvent} />;
  };

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DSDialogTitle>
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DSTypography variant="h6">
            Calendar Event Details - {formattedDate}
          </DSTypography>
          {!isPersonalFeedHistory(calendarEvent) && (
            <Edit onClick={handleModeSwitch} sx={{ cursor: "pointer" }} />
          )}
        </DSBox>
      </DSDialogTitle>
      <DialogContent />
    </DSDialog>
  );
};

export { CalendarEventDialogMode };
export default CalendarEventDialog;
